<script setup lang="ts">
import type { PortalFooterLink, PortalFooterSocialLink } from '.';

import { pilarHomesInstagram } from '~/constants';
import { MixpanelEvents, MixpanelProps } from '~/core/service/mixpanel';

import InstagramIcon from '~icons/pilar/instagram';

import TextLink from '../ui/text-link/TextLink.vue';

export interface FooterProps {
	links?: PortalFooterLink[];
	socials?: PortalFooterSocialLink[];
}

withDefaults(defineProps<FooterProps>(), {
	links: () => [
		{
			label: 'Política de privacidade',
			route: '/politica-de-privacidade'
		},
		{ label: 'Termos e condições de uso', route: '/termos-de-uso' },
		{ label: 'Mapa do site', route: '/sitemap/sitemap.xml', server: true }
	],
	socials: () => [{ icon: InstagramIcon, route: pilarHomesInstagram }]
});
const route = useRoute();
const { $mixpanel } = useNuxtApp();

const handleLinkClick = (linkRoute: string, linkText: string) => {
	$mixpanel.track(MixpanelEvents.LINK_CLICKED, {
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: 'footer',
		[MixpanelProps.DESTINATION_URL]: linkRoute,
		[MixpanelProps.LINK_CATEGORY]: 'footer-links',
		[MixpanelProps.LINK_TEXT]: linkText
	});
};
</script>

<template>
	<footer class="flex flex-col gap-8 py-8 text-white lg:py-11">
		<div class="flex flex-col items-start gap-4 xl:flex-row">
			<div
				v-for="link in links"
				:key="`footer-links: ${link.label}`"
				class="contents"
			>
				<NuxtLink
					v-if="!link.server"
					:to="link.route"
					@click="handleLinkClick(link.route, link.label)"
				>
					<TextLink
						as="p"
						variant="dark"
						class="whitespace-nowrap text-sm font-normal leading-[150%] underline-offset-[6px]"
					>
						{{ link.label }}
					</TextLink>
				</NuxtLink>

				<TextLink
					v-else
					as="a"
					:href="link.route"
					variant="dark"
					class="whitespace-nowrap text-sm font-normal leading-[150%] underline-offset-[6px]"
				>
					{{ link.label }}
				</TextLink>
			</div>
		</div>

		<div
			class="flex flex-col items-start justify-start gap-8 md:flex-row md:items-end md:justify-between"
		>
			<div class="flex items-center justify-start gap-6 md:order-1">
				<NuxtLink
					v-for="social in socials"
					:key="`footer-social: ${social.route}`"
					:to="social.route"
					target="_blank"
				>
					<component
						:is="social.icon"
						class="size-6 cursor-pointer text-white"
					/>
				</NuxtLink>
			</div>

			<div class="flex items-center justify-start gap-3">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="22"
					viewBox="0 0 24 22"
					fill="none"
				>
					<path
						d="M6 0.637451L0 11L6 21.3626H18L24 11L18 0.637451H6ZM15.9331 20.0476L2.17575 12.1277L17.8912 3.08013L15.9331 20.0476Z"
						fill="white"
					/>
				</svg>
				<p class="text-xs font-normal leading-[150%] text-white-dark-4">
					© 2025 Pilar. CRECI 39836-J. <br class="lg:hidden" />
					Todos os direitos reservados.
				</p>
			</div>
		</div>
	</footer>
</template>
